import { TrendlineV1 } from '@andywilliams/indicators';

const toTimestamp = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (!value && value !== 0) {
    return null;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const pickNumeric = (candidate, keys) => {
  for (const key of keys) {
    if (candidate && Object.prototype.hasOwnProperty.call(candidate, key)) {
      const num = Number(candidate[key]);
      if (Number.isFinite(num)) {
        return num;
      }
    }
  }
  return null;
};

export const normalizeCandlesForTrendlines = (candles = []) =>
  candles
    .map((candle) => {
      const timestamp = toTimestamp(
        candle?.t
        ?? candle?.timestamp
        ?? candle?.time
        ?? candle?.date
        ?? candle?.datetime
        ?? candle?.weekStartDate,
      );
      const open = pickNumeric(candle, ['open', 'o', 'Open', 'openPrice', 'open_price', 'O']);
      const high = pickNumeric(candle, ['high', 'h', 'High', 'highPrice', 'high_price', 'H']);
      const low = pickNumeric(candle, ['low', 'l', 'Low', 'lowPrice', 'low_price', 'L']);
      const close = pickNumeric(candle, ['close', 'c', 'Close', 'closePrice', 'close_price', 'C']);

      if (
        !Number.isFinite(timestamp)
        || open === null
        || high === null
        || low === null
        || close === null
      ) {
        return null;
      }

      return {
        t: timestamp,
        o: open,
        h: high,
        l: low,
        c: close,
      };
    })
    .filter(Boolean);

const toIsoString = (timestamp) => {
  if (!Number.isFinite(timestamp)) {
    return undefined;
  }
  return new Date(timestamp).toISOString();
};

const resolveLineTimestamp = (point, index, candles) => {
  if (point && Number.isFinite(point.t)) {
    return point.t;
  }

  if (typeof point?.date === 'string') {
    const parsed = new Date(point.date).getTime();
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  if (Number.isFinite(index) && Array.isArray(candles) && candles[index]) {
    const fallback = candles[index].t;
    if (Number.isFinite(fallback)) {
      return fallback;
    }
  }

  return undefined;
};

export const formatTrendlinesForDisplay = (trendlines = [], candles = [], timeframe) =>
  trendlines.map((line) => {
    const startTimestamp = resolveLineTimestamp(line.start, line.startIndex, candles);
    const endTimestamp = resolveLineTimestamp(line.end, line.endIndex, candles);
    const startDate = toIsoString(startTimestamp);
    const endDate = toIsoString(endTimestamp);

    const startValue =
      Number.isFinite(line.start?.price) ? line.start.price : line.startPrice ?? line.start?.value;
    const endValue =
      Number.isFinite(line.end?.price) ? line.end.price : line.endPrice ?? line.end?.value;

    const meta =
      line.meta && line.meta.breachedAt
        ? {
            ...line.meta,
            breachedAt: {
              ...line.meta.breachedAt,
              date: toIsoString(line.meta.breachedAt.t),
            },
          }
        : line.meta;

    return {
      ...line,
      timeframe: line.timeframe ?? timeframe,
      startDate: startDate ?? line.startDate,
      endDate: endDate ?? line.endDate,
      start: {
        ...line.start,
        date: startDate ?? line.start?.date,
        value: Number.isFinite(startValue) ? startValue : undefined,
      },
      end: {
        ...line.end,
        date: endDate ?? line.end?.date,
        value: Number.isFinite(endValue) ? endValue : undefined,
      },
      meta,
    };
  });

// V1 is the only served trendline engine (DWLF-322 retired V2).
export const computeTrendlinesForChart = ({
  candles,
  timeframe,
  swingLookbacks,
}) => {
  const normalized = normalizeCandlesForTrendlines(candles);
  if (normalized.length < 3) {
    return [];
  }
  const swingLookback =
    swingLookbacks?.[timeframe] ??
    (timeframe === 'weekly' ? 3 : timeframe === 'hourly' ? 5 : 5);

  const result = TrendlineV1.computeTrendlinesV1(normalized, { swingLookback });
  return formatTrendlinesForDisplay(result.trendlines, normalized, timeframe);
};

// DWLF-330: from indicators 14, TrendlineV1.detectEvents judges each break
// against the line a live run had drawn on that bar. computeTrendlinesV1 fits
// each group over all of its pivots with hindsight, so a break's own line may
// not be among the lines it returns. These are the breaks' own lines: a point
// at the line's first pivot and one at each of its breaks, every point on the
// line exactly. (The line is straight in bars, not in time, so across weekend
// or overnight gaps a two-point segment would miss the breaks between.)
// `events` are TrendlineV1.detectEvents over the same candles, so the chart
// detects once for its markers and these lines.
export const computeTrendlineBreakSegments = (candles, events) => {
  if (!Array.isArray(candles) || !Array.isArray(events)) {
    return [];
  }
  const byLine = new Map();
  events.forEach((event) => {
    const payload = event?.payload;
    const linePrice = payload?.detail?.linePrice;
    if (!payload || !Number.isInteger(payload.startIndex) || !Number.isInteger(event.index)
      || !Number.isFinite(payload.slope) || !Number.isFinite(linePrice)
      || !candles[payload.startIndex] || !candles[event.index]) {
      return;
    }
    const key = `${payload.lineType}:${payload.startIndex}:${payload.anchorIndex}`;
    if (!byLine.has(key)) {
      byLine.set(key, {
        lineType: payload.lineType,
        slope: payload.slope,
        points: [{
          index: payload.startIndex,
          t: candles[payload.startIndex].t,
          value: linePrice - payload.slope * (event.index - payload.startIndex),
        }],
      });
    }
    const { points } = byLine.get(key);
    if (points[points.length - 1].index !== event.index) {
      points.push({ index: event.index, t: candles[event.index].t, value: linePrice });
    }
  });
  return [...byLine.values()];
};
